import org.w3c.dom.Document

/**
 * The stylesheet that hides Gridstack's corner resize handles on phones.
 *
 * On a phone a widget is resized by long-pressing its header, which puts it in edit
 * mode (`gs-edit-active`); only then do the handles appear. Without that, the handles
 * sit under every fingertip that meant to scroll.
 *
 * The rule is wrapped in a **max-width media query**, and that is the whole point of
 * this file. It used to be a bare `display: none !important` injected into
 * `document.head` at mount time, on the strength of one `window.innerWidth < 768`
 * reading — and it was never removed. So any moment the dashboard happened to mount
 * under 768 CSS px, the handles were hidden **for the life of the page, at every
 * width**: the desktop grid kept its drag, kept its lock state, kept saying it was
 * unlocked, and simply had no corner handles. Browser zoom is enough to trigger it —
 * 200% on a 1512-px display reads as a 756-px viewport — and because the style lives
 * in `<head>`, which survives every SPA navigation, switching tabs did not clear it.
 * Only a reload did.
 *
 * A media query cannot leak: the browser re-evaluates it on every resize and zoom, so
 * the rule applies exactly when the viewport is phone-sized and never otherwise, no
 * matter when it was installed.
 *
 * The breakpoint mirrors [ResponsiveUtils.MOBILE_BREAKPOINT] (`< 768`), expressed as
 * `max-width: 767.98px` so the two agree on fractional viewport widths.
 */
object MobileResizeHandles {

    const val STYLE_ID = "mobile-resize-style"

    val CSS = """
        @media (max-width: ${ResponsiveUtils.MOBILE_BREAKPOINT - 0.02}px) {
            .grid-stack > .grid-stack-item > .ui-resizable-handle { display: none !important; }
            .grid-stack > .grid-stack-item.gs-edit-active > .ui-resizable-handle { display: block !important; }
        }
    """.trimIndent()

    /**
     * Install the stylesheet into [doc], at most once. The dashboard is rebuilt on every
     * visit, so without the guard each visit appended another copy.
     */
    fun install(doc: Document) {
        if (doc.getElementById(STYLE_ID) != null) return
        // No `as HTMLElement`: `doc` may be a different realm than this script's
        // (an iframe under test), where an instanceof check against our own
        // HTMLElement fails. `id` and `textContent` are declared on Element/Node.
        val style = doc.createElement("style")
        style.id = STYLE_ID
        style.textContent = CSS
        doc.head?.appendChild(style)
    }
}
